import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import CheckinQRPage from "../components/CheckinQRPage";
import { CheckCircle, Clock, XCircle, ArrowRight, FilePdf, ChatCircleDots, X, QrCode } from "@phosphor-icons/react";

const STATUS_STYLES = {
  confirmed: { icon: CheckCircle, label: "Confirmed", color: "text-emerald-700 bg-emerald-50" },
  pending_payment: { icon: Clock, label: "Pending Payment", color: "text-amber-700 bg-amber-50" },
  cancelled: { icon: XCircle, label: "Cancelled", color: "text-red-700 bg-red-50" },
};

export default function BookingsPage() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(null);
  const [showQR, setShowQR] = useState({});

  const load = () =>
    api.get("/bookings/mine").then(({ data }) => setBookings(data)).finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const doCancel = async (id) => {
    if (!window.confirm("Cancel this booking?\n\nRefund policy:\n• >48h before check-in: 100%\n• 24-48h: 50%\n• <24h: 0%")) return;
    setCancelling(id);
    try {
      const { data } = await api.post(`/bookings/${id}/cancel`);
      toast.success(data.message || "Cancelled");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Cancel failed");
    } finally { setCancelling(null); }
  };

  const invoiceUrl = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/bookings/${id}/invoice.pdf`;

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12">
      <p className="text-xs tracking-[0.3em] uppercase text-gold mb-3">Your journey</p>
      <h1 className="font-serif text-4xl lg:text-5xl text-midnight mb-10">My Bookings</h1>

      {loading ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-40 bg-stone rounded-2xl animate-pulse" />
          ))}
        </div>
      ) : bookings.length === 0 ? (
        <div className="text-center py-24 bg-white rounded-2xl border border-black/5" data-testid="no-bookings">
          <p className="font-serif text-2xl text-midnight mb-3">No trips yet</p>
          <p className="text-neutral-500 mb-6">Ready to discover your next favourite stay?</p>
          <Link to="/hotels" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-midnight text-bone hover:bg-midnight-hover transition-colors">
            Browse stays <ArrowRight size={16} />
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {bookings.map((b) => {
            const status = STATUS_STYLES[b.status] || STATUS_STYLES.pending_payment;
            const StatusIcon = status.icon;
            return (
              <div
                key={b.id}
                className="bg-white border border-black/5 rounded-2xl overflow-hidden flex flex-col md:flex-row"
                data-testid={`booking-${b.id}`}
              >
                {b.hotel_image && (
                  <div className="md:w-64 h-48 md:h-auto overflow-hidden shrink-0">
                    <img src={b.hotel_image} alt="" className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="p-6 flex-1 flex flex-col md:flex-row md:items-center gap-6">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <h3 className="font-serif text-xl text-midnight">{b.hotel_name}</h3>
                      <span className={`text-xs tracking-widest uppercase px-2 py-1 rounded-full flex items-center gap-1 ${status.color}`}>
                        <StatusIcon size={12} weight="fill" />
                        {status.label}
                      </span>
                    </div>
                    <p className="text-sm text-neutral-500 mb-3">{b.hotel_city}</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-[10px] uppercase tracking-widest text-neutral-500">Check-in</p>
                        <p className="text-midnight">{new Date(b.check_in).toLocaleDateString("en-IN")}</p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-widest text-neutral-500">Check-out</p>
                        <p className="text-midnight">{new Date(b.check_out).toLocaleDateString("en-IN")}</p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-widest text-neutral-500">Guests</p>
                        <p className="text-midnight">{b.guests}</p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-widest text-neutral-500">Rooms</p>
                        <p className="text-midnight">{b.rooms}</p>
                      </div>
                    </div>
                    {b.status === "cancelled" && b.refund_amount > 0 && (
                      <p className="text-xs text-emerald-700 mt-3">
                        Refund ₹{b.refund_amount.toLocaleString("en-IN")} ({b.refund_percent}%) processing in 5-7 business days
                      </p>
                    )}
                  </div>
                  <div className="text-right md:border-l md:pl-6 md:border-black/10 flex flex-col gap-2 items-end">
                    <p className="text-[10px] uppercase tracking-widest text-neutral-500">Total</p>
                    <p className="font-serif text-2xl text-midnight">
                      ₹{b.total_amount.toLocaleString("en-IN")}
                    </p>
                    {b.status === "pending_payment" && (
                      <Link to={`/checkout/${b.id}`} className="text-xs tracking-widest uppercase text-gold hover:text-midnight" data-testid={`pay-now-${b.id}`}>
                        Pay now →
                      </Link>
                    )}
                    {b.status === "confirmed" && (
                      <>
                        <a href={invoiceUrl(b.id)} target="_blank" rel="noreferrer"
                           className="text-xs tracking-widest uppercase text-gold hover:text-midnight inline-flex items-center gap-1"
                           data-testid={`invoice-${b.id}`}>
                          <FilePdf size={12} weight="duotone" /> Invoice
                        </a>
                        <button onClick={() => setShowQR((s) => ({ ...s, [b.id]: !s[b.id] }))}
                                className="text-xs tracking-widest uppercase text-gold hover:text-midnight inline-flex items-center gap-1"
                                data-testid={`toggle-qr-${b.id}`}>
                          <QrCode size={12} weight="duotone" /> {showQR[b.id] ? "Hide QR" : "Check-in QR"}
                        </button>
                        <Link to={`/chat/booking/${b.id}`}
                              className="text-xs tracking-widest uppercase text-gold hover:text-midnight inline-flex items-center gap-1"
                              data-testid={`chat-booking-${b.id}`}>
                          <ChatCircleDots size={12} weight="duotone" /> Chat hotel
                        </Link>
                        <button onClick={() => doCancel(b.id)} disabled={cancelling === b.id}
                                className="text-xs tracking-widest uppercase text-red-600 hover:text-red-800 inline-flex items-center gap-1 disabled:opacity-50"
                                data-testid={`cancel-${b.id}`}>
                          <X size={12} weight="bold" /> {cancelling === b.id ? "Cancelling..." : "Cancel"}
                        </button>
                      </>
                    )}
                  </div>
                </div>
                {showQR[b.id] && <div className="px-6 pb-6"><CheckinQRPage bookingId={b.id} /></div>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
